import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha, keyframes, styled } from "@mui/material/styles";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import LocalPizzaIcon from "@mui/icons-material/LocalPizza";
import RamenDiningIcon from "@mui/icons-material/RamenDining";
import SoupKitchenIcon from "@mui/icons-material/SoupKitchen";
import { blue, green, orange, red, yellow } from "@mui/material/colors";
import { CREATING_ORDER_PHRASES } from "../../types/order";

// Define keyframes for animations
const bounce = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-15px); }
`;

const rotate = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const pulse = keyframes`
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.2); opacity: 0.8; }
    100% { transform: scale(1); opacity: 1; }
`;

const wave = keyframes`
    0%, 100% { transform: translateX(0); }
    50% { transform: translateX(10px); }
`;

// Define interface for custom props (optional, but useful)
const AnimatedIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "delay" && prop !== "animationType",
})(({ theme, delay, animationType }) => {
  let animationStr = "";
  if (animationType === "bounce") {
    animationStr = `${bounce} 1.5s infinite ease-in-out ${delay}s`;
  } else if (animationType === "rotate") {
    animationStr = `${rotate} 3s infinite linear ${delay}s`;
  } else if (animationType === "pulse") {
    animationStr = `${pulse} 2s infinite ease-in-out ${delay}s`;
  } else if (animationType === "wave") {
    animationStr = `${wave} 2s infinite ease-in-out ${delay}s`;
  }
  return {
    margin: theme.spacing(0, 1.5),
    animation: animationStr,
  };
});

const LoadingDot = styled(Box)(({ theme, delay }) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  margin: theme.spacing(0, 0.5),
  backgroundColor: yellow[700],
  "@keyframes pulseDot": {
    "0%": { transform: "scale(1)", opacity: 1 },
    "50%": { transform: "scale(1.2)", opacity: 0.7 },
    "100%": { transform: "scale(1)", opacity: 1 },
  },
  animation: "pulseDot 1.5s infinite ease-in-out",
  animationDelay: `${delay}s`,
}));

const PulsingText = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  fontWeight: 500,
  color: theme.palette.text.primary,
  "@keyframes textPulse": {
    "0%": { opacity: 0.8 },
    "50%": { opacity: 1 },
    "100%": { opacity: 0.8 },
  },
  animation: "textPulse 2s infinite ease-in-out",
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  backgroundColor: alpha("#fff", 0.95),
  borderRadius: 16,
  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
  width: 440,
  maxWidth: "90vw",
  zIndex: 10000,
  [theme.breakpoints.down("sm")]: {
    width: "90%",
  },
}));

const IconsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  height: 100,
  width: "100%",
  position: "relative",
}));

const steam = keyframes`
  0% { transform: translateY(0) scale(1); opacity: 0.7; }
  100% { transform: translateY(-20px) scale(1.5); opacity: 0; }
`;

const FoodLoadingAnimation = () => {
  const [loadingText, setLoadingText] = useState("Preparando seu pedido");

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % CREATING_ORDER_PHRASES.length;
      setLoadingText(CREATING_ORDER_PHRASES[index]);
    }, 2200);

    return () => clearInterval(interval);
  }, []);

  const foodIcons = [
    {
      icon: <FastfoodIcon sx={{ fontSize: 40, color: orange[700] }} />,
      animation: "bounce",
      delay: 0,
    },
    {
      icon: <LocalPizzaIcon sx={{ fontSize: 40, color: red[500] }} />,
      animation: "rotate",
      delay: 0.2,
    },
    {
      icon: <LunchDiningIcon sx={{ fontSize: 40, color: yellow[800] }} />,
      animation: "pulse",
      delay: 0.4,
    },
    {
      icon: <RamenDiningIcon sx={{ fontSize: 40, color: orange[500] }} />,
      animation: "bounce",
      delay: 0.6,
    },
    {
      icon: <SoupKitchenIcon sx={{ fontSize: 40, color: green[600] }} />,
      animation: "wave",
      delay: 0.8,
    },
    {
      icon: <RestaurantIcon sx={{ fontSize: 40, color: blue[700] }} />,
      animation: "pulse",
      delay: 1.0,
    },
  ];

  return (
    <LoadingContainer>
      {/* Steam animation on top */}
      <Box
        sx={{
          position: "absolute",
          top: -20,
          left: "50%",
          transform: "translateX(-50%)",
        }}
      >
        {[...Array(3)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: 10,
              height: 20,
              backgroundColor: "rgba(255,255,255,0.7)",
              borderRadius: "50%",
              left: i * 15 - 15,
              animation: `${steam} 1.5s infinite`,
              animationDelay: `${i * 0.4}s`,
            }}
          />
        ))}
      </Box>

      {/* Food icons animation */}
      <IconsContainer>
        {foodIcons.map((item, index) => (
          <AnimatedIcon
            key={index}
            delay={item.delay}
            animationType={item.animation}
          >
            {item.icon}
          </AnimatedIcon>
        ))}
      </IconsContainer>

      {/* Loading text */}
      <PulsingText variant="h6" sx={{ mb: 2 }}>
        {loadingText}
      </PulsingText>

      {/* Progress dots */}
      <Box sx={{ display: "flex", mt: 1 }}>
        {[0, 1, 2].map((dot) => (
          <LoadingDot key={dot} delay={dot * 0.3} />
        ))}
      </Box>
    </LoadingContainer>
  );
};

export default FoodLoadingAnimation;
